import { Injectable } from '@angular/core';
import { CreateTaskPayload, TaskRepository } from 'src/core/tasks/domain/task-repository';
import { CreateTaskRequest } from './create-task-request';
import { QueueService } from 'src/core/queue/domain/queue.service';
import { LocalTask } from 'src/core/master-data/domain/local-task';
import { CreateTaskQueueElement } from 'src/core/queue/domain/elements/create-task-queue-element';
import { TaskService } from 'src/core/tasks/domain/task.service';
import { QueueElement, QueueElementError } from 'src/core/queue/domain/queue-element';
import { GarbageCollectorService } from 'src/core/garbage-collector/domain/garbage-collector.service';
import { ConnectionException } from 'src/core/common/domain/exceptions/connection-exception';
import { UnauthorizedException } from 'src/core/authentication/domain/exceptions/unauthorized-exception';
import { Nullable } from 'src/core/common/domain/types/types';

@Injectable({
  providedIn: 'any',
})
export class CreateTask {
  private task: LocalTask;
  private isCreatedToServer = false;
  private createdTaskId: Nullable<string> = null;
  private isRetry = false;

  constructor(
    private readonly taskRepository: TaskRepository,
    private readonly taskService: TaskService,
    private readonly queueService: QueueService,
    private readonly garbageCollector: GarbageCollectorService
  ) {}

  async execute(request: CreateTaskRequest): Promise<void> {
    this.task = request.task;
    const queueElement = request.queueElement;
    this.isRetry = queueElement !== null;
    this.isCreatedToServer = queueElement?.operationStatus.isCreatedToServer ?? false;
    this.createdTaskId = queueElement?.operationStatus.createdTaskId ?? null;

    try {
      if (!this.isRetry) {
        await this.saveTaskIntoLocalDevice();
      }

      const createdTaskId = await this.createTaskInExternalServer();
      if (this.task.isLocal()) {
        // Read before updating: updateTaskIntoLocalDevice() replaces the id on this very instance,
        // and the queue still holds elements pointing at the local one.
        const localTaskId = this.task.id;

        await this.garbageCollector.addDirectoryToDelete(
          this.taskService.getAttachmentsDirectoryForTaskId(localTaskId)
        );
        await this.updateTaskIntoLocalDevice(createdTaskId);
        await this.queueService.updateLocalTaskReferences(localTaskId, createdTaskId);
      }
    } catch (exception) {
      if (exception instanceof ConnectionException) {
        // a retried element is still in the queue, and enqueueing another would create the task twice
        if (!this.isRetry) {
          await this.enqueueOperation();
        }
        throw exception; // propagate exception, since it must be handled differently
      }

      // exceptions during retrying do not enqueue or rollback the task, but store error to display in list
      if (this.isRetry) {
        await this.storeErrorsInQueueElement(exception, queueElement);
        throw exception; // propagate exception without doing anything else
      }

      if (this.isCreatedToServer) {
        // the server already has the task, so the queue finishes the local steps instead of leaving an orphan
        await this.enqueueOperation();
      }
      throw exception; // propagate exception
    }
  }

  /**
   * The id is kept in the queue element as soon as the server answers, so a retry after a failed local step
   * does not create the task a second time.
   */
  private async createTaskInExternalServer(): Promise<string> {
    if (this.isCreatedToServer) {
      return this.createdTaskId;
    }
    const response = await this.taskRepository.create(this.getPayloadFromTask());
    this.isCreatedToServer = true;
    this.createdTaskId = response.taskId;

    return response.taskId;
  }

  private getPayloadFromTask(): CreateTaskPayload {
    const payload: CreateTaskPayload = {
      expectedDate: this.task.date.expected,
      createdAt: this.task.createdAt,
      ambitId: this.task.ambit.value.place.id,
      workTypeId: this.task.workType.id,
      subject: this.task.subject,
      attachments: this.task.attachments,
    };

    if (this.task.taskType) {
      payload.taskTypeId = this.task.taskType.id;
    }

    if (this.task.priority) {
      payload.priorityId = this.task.priority.id;
    }

    return payload;
  }

  private async storeErrorsInQueueElement(exception: unknown, queueElement: CreateTaskQueueElement) {
    // an expired session is handled by the session flow, it says nothing about this operation
    if (exception instanceof UnauthorizedException) {
      return;
    }
    await this.updateQueueElementWithErrors(queueElement, QueueElement.errorsFromException(exception));
  }

  private async updateQueueElementWithErrors(queueElement: CreateTaskQueueElement, errors: Array<QueueElementError>) {
    const operationStatus = {
      isCreatedToServer: this.isCreatedToServer,
      createdTaskId: this.createdTaskId,
    };
    queueElement.update(this.task, operationStatus, errors);
    await this.queueService.updateQueueElement(queueElement);
  }

  private async saveTaskIntoLocalDevice() {
    await this.taskService.saveTask(this.task);
  }

  private async enqueueOperation(errors: Array<QueueElementError> = []) {
    const operationStatus = {
      isCreatedToServer: this.isCreatedToServer,
      createdTaskId: this.createdTaskId,
    };
    await this.queueService.enqueue(CreateTaskQueueElement.fromTask(this.task, operationStatus, errors));
  }

  private async updateTaskIntoLocalDevice(newTaskId: string) {
    await this.taskService.removeTask(this.task);
    this.task.changeId(newTaskId);
    await this.taskService.saveTask(this.task);
  }
}
